// Migrated from Dayforce: UI\Framework\Scripts\Framework\Modules\Log.ts

// import { JsonUtil } from "./utils/json-util";
import DojoConfig from "./utils/dojo-config";
/**
 * Retrieves a formatted string representing the current date/time
 * @private
 * @return {string} The formatted date
 */
function _getDateStr() {
    var date: Date = new Date();
    //date formatted as dd/MM/YYYY HH:mm:ss-ttt
    var day: string = _padNumber(date.getDate(), 2, "0");
    var month: string = _padNumber(date.getMonth() + 1, 2, "0");
    var hour: string = _padNumber(date.getHours(), 2, "0");
    var minute: string = _padNumber(date.getMinutes(), 2, "0");
    var second: string = _padNumber(date.getSeconds(), 2, "0");
    var millisecond: string = _padNumber(date.getMilliseconds(), 3, "0");
    var dateStr: string =
        day +
        "/" +
        month +
        "/" +
        date.getFullYear() +
        " " +
        hour +
        ":" +
        minute +
        ":" +
        second +
        "." +
        millisecond;

    return dateStr;
}

/**
 * Private function to format log messages before they are written to the console
 * @private
 */
function _getFormattedMessage(
    moduleName: string,
    functionName: string,
    message?: any
) {
    var currDate: string = _getDateStr();
    var msg: string =
        "[" + moduleName + "] " + currDate + " (" + functionName + ")";
    if (message) {
        if (typeof message == "boolean" || typeof message == "number") {
            message = message.toString();
        } else if (typeof message != "string") {
            //if not a string, bool or number - must be object. Stringify it.
            // OLD - using native JSON stringify to decouple JsonUtil difference is date formatting 
            // message = JsonUtil.stringify(message);
            // NEW
            message = JSON.stringify(message);
        }

        msg += " - " + message;
    }
    return msg;
}

/**
 * Pads the given number with a specified length and using a specified padding character
 * @private
 * @param {number} input The number to pad
 * @param {number} length The length to enforce
 * @param {string} paddingChar The string to pad with
 * @return {string} The padded number as a string
 */
function _padNumber(input: number, length: number, paddingChar: string) {
    var str: string = input.toString();
    while (str.length < length) {
        str = paddingChar + str;
    }
    return str;
}

/**
 * Traces a normal log message to the console if isDebug is true
 * @param moduleName The name of the AMD module
 * @param functionName The name of the function that you are logging from
 * @param message The message to write to the console. DO NOT PASS CIRCULAR JSON OBJECTS INTO THIS
 */
function log(moduleName: string, functionName: string, message?: any): void {
    if (DojoConfig.isDebug === true) {
        if (console) {
            console.log(
                _getFormattedMessage(moduleName, functionName, message)
            );
        }
    }
}

/**
 * Traces a warning-level message to the browser console (if supported). If
 * warning is not supported by the current browser's console, a normal message will be logged with
 * metadata specifying that it is a warning
 * @param {string} moduleName The name of the AMD module
 * @param {string} functionName The name of the function that you are logging from
 * @param {*} message The message to write to the console. DO NOT PASS CIRCULAR JSON OBJECTS INTO THIS
 */
function warn(moduleName: string, functionName: string, message?: any) {
    if (DojoConfig.isDebug === true || DojoConfig.isQA) {
        if (console) {
            // #1036436 Not sure why we're not outputting the formatted message instead, 
            // I think it's a mistake from the old implementation.
            // We can revisit in later phases. Keep in mind to update unit test accordingly by then.
            const msg: string = _getFormattedMessage(
                moduleName,
                functionName,
                message
            );
            if (console.warn) {
                console.warn(message);
            } else {
                console.log(message + " WARNING");
            }
        }
    }
}

/**
 * Traces an error message to the browser console, if supported.
 * If the current browser doesn't support error logging, a normal message will be logged with metadata
 * specifying that this is an error
 * @param {string} moduleName The name of the AMD module
 * @param {string} functionName The name of the function that you are logging from
 * @param {*} message The message to write to the console. DO NOT PASS CIRCULAR JSON OBJECTS INTO THIS
 */
function error(moduleName: string, functionName: string, message?: any) {
    //Always log errors. No need to check isDebug
    if (console) {
        if (message && message.toString) message = message.toString();

        var msg: string = _getFormattedMessage(
            moduleName,
            functionName,
            message
        );
        if (console.error) {
            console.error(msg);
        } else {
            console.log(msg + " ERROR");
        }
    }
}

/**
 * Traces to the console under a normal message irrespective is isDebug is true or not
 * @param {string} moduleName The name of the AMD module
 * @param {string} functionName The name of the function that you are logging from
 * @param {*} message The message to write to the console. DO NOT PASS CIRCULAR JSON OBJECTS INTO THIS
 */
function alwaysOn(moduleName: string, functionName: string, message?: any) {
    var msg: string = _getFormattedMessage(moduleName, functionName, message);
    if (console) {
        console.log(msg);
    }
}

/**
 * Log is the default entry point to log trace messages to the browser console
 * Logging is controlled by the isDebug flag in the dojo configuration, and further
 * is enabled when LogMode is not null in the DFSessionInfo (see _AppShell.cshtml)
 * @module Log
 * @interface
 */
export const Log = {
    log,
    warn,
    error,
    alwaysOn,
};
